import {createFileRoute} from "@tanstack/react-router";
import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {
    PUBLIC_SUBSCRIPTION_PLANS__QUERY_KEY,
    WORKSPACE__INVOICES__QUERY_KEY,
    WORKSPACE__IS_ANOTHER_PLAN_UPCOMING__QUERY_KEY,
    WORKSPACE__RESOURCE_INFORMATION__QUERY_KEY,
} from "~/utilities/queryKeys";
import {getCountriesWithCaching} from "~/utilities/queries";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {publicSubscriptionPlanZodValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/SubscriptionPlan";
import type {PublicSubscriptionPlan} from "~/domainModels/subscriptionPlans";
import {
    workspaceResourceInformationZodValidator,
    type WorkspaceResourceInformation,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PricingLimits";
import {
    invoiceWithSubscriptionPlanDetailsZodValidator,
    type InvoiceWithSubscriptionPlanDetails,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/Invoice";

export type BillingPageInvoice = InvoiceWithSubscriptionPlanDetails;

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_defaultScaffold/billing",
)({
    validateSearch: zod.object({
        openPlanSelector: zod.string().optional(),
        canCloseCheckoutModal: zod.boolean().optional(),
        isPaymentFailed: zod.boolean().optional(),
        subscriptionPlanId: uuidZodValidator.optional(),
        paymentCurrency: uuidZodValidator.optional(),
        paymentFrequency: uuidZodValidator.optional(),
    }),
    loader: async ({context}) => {
        const countriesResult = await getCountriesWithCaching({
            queryClient: context.queryClient,
            intellsysBaseUrl: context.intellsysBaseUrl,
        });
        if (countriesResult.success === false) {
            throw countriesResult.error;
        }
        const countries = countriesResult.data;

        const [companyPricingLimits, isAnotherPlanUpcoming, publicSubscriptionPlans, invoices] = await Promise.all([
            context.queryClient.ensureQueryData<WorkspaceResourceInformation>({
                queryKey: [WORKSPACE__RESOURCE_INFORMATION__QUERY_KEY, context.workspace.id],
                queryFn: async () => {
                    const companyPricingLimitsResult = await robustJsonResultClientFetch(
                        `${context.intellsysBaseUrl}/sub-system/pluto/api/ottosync-subscription/get-company-pricing-limits`,
                        {
                            body: JSON.stringify({}),
                            headers: {
                                Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                                "Content-Type": "application/json",
                                "X-Workspace-Id": context.workspace.id,
                            },
                            method: "POST",
                        },
                        zod.object({
                            companyResourceInformation: workspaceResourceInformationZodValidator,
                        }),
                    );
                    if (companyPricingLimitsResult.success === false) {
                        throw companyPricingLimitsResult.error;
                    }

                    return companyPricingLimitsResult.data.companyResourceInformation;
                },
                retry: false,
                staleTime: Infinity,
            }),
            context.queryClient.ensureQueryData<boolean>({
                queryKey: [WORKSPACE__IS_ANOTHER_PLAN_UPCOMING__QUERY_KEY, context.workspace.id],
                queryFn: async () => {
                    const upcomingPlanResult = await robustJsonResultClientFetch(
                        `${context.intellsysBaseUrl}/sub-system/pluto/api/ottosync-subscription/get-is-another-plan-upcoming`,
                        {
                            body: JSON.stringify({}),
                            headers: {
                                Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                                "Content-Type": "application/json",
                                "X-Workspace-Id": context.workspace.id,
                            },
                            method: "POST",
                        },
                        zod.object({
                            isAnotherPlanUpcoming: zod.boolean(),
                        }),
                    );
                    if (upcomingPlanResult.success === false) {
                        throw upcomingPlanResult.error;
                    }

                    return upcomingPlanResult.data.isAnotherPlanUpcoming;
                },
                retry: false,
                staleTime: Infinity,
            }),
            context.queryClient.ensureQueryData<Array<PublicSubscriptionPlan>>({
                queryKey: [PUBLIC_SUBSCRIPTION_PLANS__QUERY_KEY],
                queryFn: async () => {
                    const publicSubscriptionPlansResult = await robustJsonResultClientFetch(
                        `${context.intellsysBaseUrl}/sub-system/pluto/api/ottosync-subscription/get-all-public-subscription-plans`,
                        {
                            body: JSON.stringify({}),
                            headers: {
                                "Content-Type": "application/json",
                            },
                            method: "POST",
                        },
                        zod.object({
                            publicSubscriptionPlans: zod.array(publicSubscriptionPlanZodValidator),
                        }),
                    );
                    if (publicSubscriptionPlansResult.success === false) {
                        throw publicSubscriptionPlansResult.error;
                    }

                    return publicSubscriptionPlansResult.data.publicSubscriptionPlans.map((plan) => ({
                        ...plan,
                        isFeatured: plan.isFeatured ?? false,
                    }));
                },
                retry: false,
                staleTime: Infinity,
            }),
            context.queryClient.ensureQueryData<Array<BillingPageInvoice>>({
                queryKey: [WORKSPACE__INVOICES__QUERY_KEY, context.workspace.id],
                queryFn: async () => {
                    const invoicesResult = await robustJsonResultClientFetch(
                        `${context.intellsysBaseUrl}/sub-system/pluto/api/ottosync-subscription/pricing/get-invoices-for-company`,
                        {
                            body: JSON.stringify({}),
                            headers: {
                                Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                                "Content-Type": "application/json",
                                "X-Workspace-Id": context.workspace.id,
                            },
                            method: "POST",
                        },
                        zod.array(invoiceWithSubscriptionPlanDetailsZodValidator),
                    );
                    if (invoicesResult.success === false) {
                        throw invoicesResult.error;
                    }

                    return invoicesResult.data;
                },
                retry: false,
                staleTime: Infinity,
            }),
        ]);

        const subscriptionDetails = context.subscriptionDetails;

        return {
            workspace: context.workspace,
            subscriptionDetails: subscriptionDetails,
            intellsysBaseUrl: context.intellsysBaseUrl,
            accessToken: context.authenticationStatus.accessToken,
            pricingPlanNudgeStatus: context.pricingPlanNudgeStatus,
            countries,
            isAnotherPlanUpcoming,
            invoices,
            companyPricingLimits,
            publicSubscriptionPlans,
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
